import { useCallback, useState } from 'react';
import { ImportRepositoryDialog } from '~/components/github/ImportRepositoryDialog';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { navigateToImport } from '~/lib/client-github';
import { useGithubStore } from '~/lib/context/github';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { RepoFullName } from '~/types/github';
import { wait } from '~/utils/promises';

export function useGithubImportRepository(login: () => Promise<boolean>) {
  const github = useGithubStore();

  const [choosing, setChoosing] = useState(false);

  const startImportRepository = useCallback(async () => {
    let loggedInUser = undefined;

    if (!userStore.loggedIn.get()) {
      const loggedIn = await login();

      if (!loggedIn) {
        return;
      }

      loggedInUser = userStore.user.get();
    }

    const connected = await github.connectToGithub(loggedInUser);

    if (connected) {
      setChoosing(true);
    }
  }, [github]);

  const importRepository = useCallback(async (repo: RepoFullName) => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.GithubRepoImportStarted,
        properties: {
          repositoryName: repo.name,
          repositoryOwner: repo.owner,
        },
      },
    });

    navigateToImport(repo);

    await wait(3_000);
    setChoosing(false);
  }, []);

  const cancelImportRepository = () => setChoosing(false);

  const dialog = (
    <ImportRepositoryDialog open={choosing} onClose={cancelImportRepository} onChoose={importRepository} />
  );

  return {
    dialog,
    startImportRepository,
  };
}
